import React, { useEffect, useRef, useState } from "react";

import { enrollStudent, sendGiftWebhook } from "../../api/authService";
import {
   DATE_INPUT_PATTERN,
   DATE_INPUT_PLACEHOLDER,
   dateInputFromAny,
   formatDateInput,
   isValidDateInput,
} from "../../utils/dateInput";
import SegmentedToggle from "../Common/SegmentedToggle";
import UIToggle from "../Common/UIToggle";
import { closePopup as closePopupUI } from "../Common/popupUIStore";

const LS_KEY = "enroll_staff_popup_draft_v1";

const SEX_OPTIONS = [
   { value: "M", label: "Masculin" },
   { value: "F", label: "Feminin" },
];

const GEARBOX_OPTIONS = [
   { value: "MECANICĂ", label: "Manual" },
   { value: "AUTOMATĂ", label: "Automat" },
];

const digits = (s = "") => String(s).replace(/\D+/g, "");
const clampLen = (s, n) => String(s || "").slice(0, n);

const toLocalPhone = (raw) => {
   const d = digits(raw);
   if (!d) return "";
   if (d.length === 8) return `0${d}`;
   return d.length === 9 ? d : String(raw || "").trim();
};

const initialForm = {
   firstName: "",
   lastName: "",
   citizenship: "Moldova",
   birthDate: "",
   sex: "M",
   idnp: "",
   email: "",
   phone: "",
   contactPerson: "",
   idDoc: { serie: "", number: "", issuedBy: "", issueDate: "" },
   gearbox: "MECANICĂ",
   source: "",
   agreeTerms: false,
   agreeGDPR: false,
};

function buildEnrollPayload(f) {
   return {
      nume: f.lastName?.trim() || "",
      prenume: f.firstName?.trim() || "",
      cetatenia: f.citizenship?.trim() || "Republica Moldova",
      email: f.email?.trim() || "",
      serieActIdentitate: f.idDoc.serie?.trim() || "",
      numarActIdentitate: f.idDoc.number?.trim() || "",
      eliberatDe: f.idDoc.issuedBy?.trim() || "",
      dataEliberare: dateInputFromAny(f.idDoc.issueDate),
      dataNasterii: dateInputFromAny(f.birthDate),
      sex: f.sex === "F" ? "F" : "M",
      idnp: f.idnp?.trim() || "",
      telefon: toLocalPhone(f.phone),
      telefonContact: toLocalPhone(f.contactPerson),
      cutie: f.gearbox === "AUTOMATĂ" ? "AUTOMATĂ" : "MECANICĂ",
      deUndeAflat: f.source?.trim() || "",
   };
}

// Secțiune a formularului: titlu mic + grila de câmpuri, ca să fie aranjate
// pe teme (date personale, act, contact, preferințe, acorduri).
function Group({ title, children }) {
   return (
      <div className="studentEnrollPopup__group">
         <h3 className="studentEnrollPopup__groupTitle">{title}</h3>
         <div className="studentEnrollPopup__grid">{children}</div>
      </div>
   );
}

function Field({ label, children, full = false, toggle = false }) {
   return (
      <label
         className={
            "studentEnrollPopup__field" +
            (full ? " studentEnrollPopup__field--full" : "") +
            (toggle ? " studentEnrollPopup__field--toggle" : "")
         }
      >
         <span className="studentEnrollPopup__fieldLabel">{label}</span>
         {children}
      </label>
   );
}

export default function StudentEnrollPopup() {
   const [form, setForm] = useState(initialForm);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState("");
   const [success, setSuccess] = useState("");
   const saveTimer = useRef(null);

   useEffect(() => {
      try {
         const raw = localStorage.getItem(LS_KEY);
         if (!raw) return;
         const parsed = JSON.parse(raw);
         if (parsed && typeof parsed === "object") {
            const merged = { ...initialForm, ...parsed };
            setForm({
               ...merged,
               birthDate: dateInputFromAny(merged.birthDate),
               idDoc: {
                  ...initialForm.idDoc,
                  ...(merged.idDoc || {}),
                  issueDate: dateInputFromAny(merged.idDoc?.issueDate),
               },
            });
         }
      } catch {
         // ignore
      }
   }, []);

   useEffect(() => {
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
         try {
            localStorage.setItem(LS_KEY, JSON.stringify(form));
         } catch {
            // ignore
         }
      }, 300);
      return () => clearTimeout(saveTimer.current);
   }, [form]);

   const setField = (name, val) => {
      setForm((prev) => ({ ...prev, [name]: val }));
      setError("");
      setSuccess("");
   };
   const setIdDoc = (name, val) => {
      setForm((prev) => ({
         ...prev,
         idDoc: { ...prev.idDoc, [name]: val },
      }));
      setError("");
      setSuccess("");
   };

   const clampDigits = (s, max = 9) => clampLen(digits(s), max);

   const numericInputProps = (field, max = 9) => ({
      value: form[field],
      onChange: (e) => setField(field, clampDigits(e.target.value, max)),
      onBeforeInput: (e) => {
         if (e.data && /\D/.test(e.data)) e.preventDefault();
         const v = e.currentTarget.value ?? "";
         if (digits(v).length >= max && e.inputType?.startsWith("insert")) {
            e.preventDefault();
         }
      },
      onPaste: (e) => {
         e.preventDefault();
         const txt =
            (e.clipboardData || window.clipboardData)?.getData("text") || "";
         setField(field, clampDigits(txt, max));
      },
      inputMode: "numeric",
      pattern: "\\d*",
      maxLength: max,
   });

   const validate = () => {
      if (!form.lastName || !form.firstName)
         return "Completează Nume și Prenume.";
      if (!form.citizenship) return "Completează Cetățenia.";
      if (!isValidDateInput(form.birthDate))
         return "Scrie Data nașterii în formatul ZZ.LL.AAAA (ex. 12.05.1995).";
      if (form.idnp.length !== 13) return "IDNP trebuie să conțină 13 cifre.";
      if (!form.idDoc.serie?.trim() || !form.idDoc.number?.trim())
         return "Completează Seria și Numărul actului de identitate.";
      if (!form.idDoc.issuedBy?.trim())
         return "Completează de cine a fost eliberat actul.";
      if (!isValidDateInput(form.idDoc.issueDate))
         return "Scrie Data eliberării în formatul ZZ.LL.AAAA (ex. 15.01.2020).";
      if (!form.email) return "Completează Email.";
      if (digits(form.phone).length < 8)
         return "Introduceți un număr de telefon valid (minim 8 cifre).";
      if (digits(form.contactPerson).length < 8)
         return "Introduceți telefonul persoanei de contact (minim 8 cifre).";
      if (!form.agreeTerms || !form.agreeGDPR)
         return "Trebuie să accepți Termenii & GDPR.";
      return null;
   };

   const clearDraft = () => {
      try {
         localStorage.removeItem(LS_KEY);
      } catch {
         // ignore
      }
      setForm(initialForm);
      setError("");
      setSuccess("Draftul a fost curățat.");
   };

   const handleSubmit = async (event) => {
      event.preventDefault();
      const err = validate();
      if (err) {
         setError(err);
         return;
      }

      setLoading(true);
      setError("");
      setSuccess("");

      try {
         const payload = buildEnrollPayload(form);
         await enrollStudent(payload);

         let giftErr = null;
         try {
            await sendGiftWebhook({
               nume: payload.nume,
               prenume: payload.prenume,
               email: payload.email,
            });
         } catch (giftError) {
            giftErr = giftError;
         }

         if (giftErr) {
            setSuccess(
               "Înscrierea a fost trimisă, dar emailul-cadou nu a putut fi expediat acum.",
            );
         } else {
            setSuccess(
               "Gata! Cererea de înscriere, contractul și emailul-cadou au fost expediate.",
            );
         }

         try {
            localStorage.removeItem(LS_KEY);
         } catch {
            // ignore
         }
         setForm(initialForm);
      } catch (submitError) {
         setError(
            `Nu am putut genera contractele: ${
               submitError?.message || "eroare necunoscută"
            }`,
         );
      } finally {
         setLoading(false);
      }
   };

   const canSubmit = form.agreeTerms && form.agreeGDPR && !loading;

   return (
      <section className="studentEnrollPopup">
         <div className="studentEnrollPopup__header">
            <h2 className="studentEnrollPopup__title">Înscriere la curs</h2>
            <p className="studentEnrollPopup__subtitle">
               Categoria B — completează toate datele
            </p>
         </div>

         <form className="studentEnrollPopup__content" onSubmit={handleSubmit}>
            <section className="studentEnrollPopup__panel">
               <Group title="Date personale">
                  <Field label="Nume">
                     <input
                        className="studentEnrollPopup__input"
                        placeholder="Popescu"
                        value={form.lastName}
                        onChange={(e) => setField("lastName", e.target.value)}
                        required
                     />
                  </Field>
                  <Field label="Prenume">
                     <input
                        className="studentEnrollPopup__input"
                        placeholder="Ion"
                        value={form.firstName}
                        onChange={(e) => setField("firstName", e.target.value)}
                        required
                     />
                  </Field>
                  <Field label="Cetățenia">
                     <input
                        className="studentEnrollPopup__input"
                        placeholder="Republica Moldova"
                        value={form.citizenship}
                        onChange={(e) =>
                           setField("citizenship", e.target.value)
                        }
                        required
                     />
                  </Field>
                  <Field label="Data nașterii">
                     <input
                        type="text"
                        className="studentEnrollPopup__input"
                        placeholder={DATE_INPUT_PLACEHOLDER}
                        value={form.birthDate}
                        onChange={(e) =>
                           setField("birthDate", formatDateInput(e.target.value))
                        }
                        inputMode="numeric"
                        pattern={DATE_INPUT_PATTERN}
                        maxLength={10}
                        title="Scrie data ca zi.lună.an, de exemplu 12.05.1995"
                        required
                     />
                  </Field>
                  <Field label="IDNP">
                     <input
                        className="studentEnrollPopup__input"
                        placeholder="1234567890123"
                        value={form.idnp}
                        onChange={(e) =>
                           setField(
                              "idnp",
                              clampLen(digits(e.target.value), 13),
                           )
                        }
                        inputMode="numeric"
                        maxLength={13}
                        required
                     />
                  </Field>
                  <div className="studentEnrollPopup__selectorCard">
                     <span className="studentEnrollPopup__fieldLabel">Gen</span>
                     <SegmentedToggle
                        name="enroll-sex"
                        value={form.sex}
                        options={SEX_OPTIONS}
                        onChange={(value) => setField("sex", value)}
                        aria-label="Gen"
                     />
                  </div>
               </Group>

               <Group title="Act de identitate">
                  <Field label="Seria act">
                     <input
                        className="studentEnrollPopup__input"
                        placeholder="A"
                        value={form.idDoc.serie}
                        onChange={(e) => setIdDoc("serie", e.target.value)}
                        required
                     />
                  </Field>
                  <Field label="Număr act">
                     <input
                        className="studentEnrollPopup__input"
                        placeholder="1234567"
                        value={form.idDoc.number}
                        onChange={(e) => setIdDoc("number", e.target.value)}
                        required
                     />
                  </Field>
                  <Field label="Eliberat de">
                     <input
                        className="studentEnrollPopup__input"
                        placeholder="ASP"
                        value={form.idDoc.issuedBy}
                        onChange={(e) => setIdDoc("issuedBy", e.target.value)}
                        required
                     />
                  </Field>
                  <Field label="Data eliberării">
                     <input
                        type="text"
                        className="studentEnrollPopup__input"
                        placeholder={DATE_INPUT_PLACEHOLDER}
                        value={form.idDoc.issueDate}
                        onChange={(e) =>
                           setIdDoc("issueDate", formatDateInput(e.target.value))
                        }
                        inputMode="numeric"
                        pattern={DATE_INPUT_PATTERN}
                        maxLength={10}
                        title="Scrie data ca zi.lună.an, de exemplu 15.01.2020"
                        required
                     />
                  </Field>
               </Group>

               <Group title="Contact">
                  <Field label="Email" full>
                     <input
                        type="email"
                        className="studentEnrollPopup__input"
                        placeholder="ion.popescu@example.com"
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        required
                     />
                  </Field>
                  <Field label="Telefon">
                     <input
                        type="tel"
                        className="studentEnrollPopup__input"
                        placeholder="069123456"
                        {...numericInputProps("phone", 9)}
                        required
                     />
                  </Field>
                  <Field label="Telefon persoană de contact">
                     <input
                        type="tel"
                        className="studentEnrollPopup__input"
                        placeholder="069654321"
                        {...numericInputProps("contactPerson", 9)}
                        required
                     />
                  </Field>
               </Group>

               <Group title="Preferințe">
                  <div className="studentEnrollPopup__selectorCard">
                     <span className="studentEnrollPopup__fieldLabel">
                        Cutie
                     </span>
                     <SegmentedToggle
                        name="enroll-gearbox"
                        value={form.gearbox}
                        options={GEARBOX_OPTIONS}
                        onChange={(value) => setField("gearbox", value)}
                        aria-label="Tip cutie"
                     />
                  </div>
                  <Field label="De unde ați aflat?">
                     <input
                        className="studentEnrollPopup__input"
                        placeholder="Google / Instagram / recomandare"
                        value={form.source}
                        onChange={(e) => setField("source", e.target.value)}
                     />
                  </Field>
               </Group>

               <Group title="Acorduri">
                  <Field label="Termeni & confidențialitate" toggle>
                     <UIToggle
                        checked={form.agreeTerms === true}
                        onChange={(checked) => setField("agreeTerms", checked)}
                        disabled={loading}
                        aria-label="Accept Termenii și Politica de confidențialitate"
                     />
                  </Field>
                  <Field label="Acord GDPR" toggle>
                     <UIToggle
                        checked={form.agreeGDPR === true}
                        onChange={(checked) => setField("agreeGDPR", checked)}
                        disabled={loading}
                        aria-label="Acord pentru prelucrarea datelor (GDPR)"
                     />
                  </Field>
               </Group>
            </section>

            {error ? (
               <div className="studentEnrollPopup__stateCard studentEnrollPopup__stateCard--error">
                  {error}
               </div>
            ) : null}
            {success ? (
               <div className="studentEnrollPopup__stateCard studentEnrollPopup__stateCard--success">
                  {success}
               </div>
            ) : null}

            <div className="studentEnrollPopup__footer">
               <button
                  type="button"
                  className="personalCreateUI__button personalCreateUI__button--secondary"
                  onClick={clearDraft}
                  disabled={loading}
               >
                  Curăță draft
               </button>
               <button
                  type="button"
                  className="studentEnrollPopup__button--ghost"
                  onClick={() => closePopupUI()}
                  disabled={loading}
               >
                  Închide
               </button>
               <button
                  type="submit"
                  className="personalCreateUI__button personalCreateUI__button--primary"
                  disabled={!canSubmit}
                  title={!canSubmit ? "Activează ambele acorduri" : "Trimite"}
                  style={{ marginLeft: "auto" }}
               >
                  {loading
                     ? "Se trimite..."
                     : !canSubmit
                       ? "Activează acordurile"
                       : "Trimite"}
               </button>
            </div>
         </form>
      </section>
   );
}
